import type { PackagingShipmentResultRow } from '@/shared/hooks/api/packagingShipments'

export type ShipmentResultCheckSummary = {
  worst: '' | 'Clear' | 'Not run' | 'Requires Attention'
  attention: number
  notRun: number
  clear: number
}

export type ShipmentResultSkuSummary = {
  packedItemCount: number
  packedItem: { id: string; name: string } | null
  supplierCount: number
  supplierName: string | null
  packagingCount: number
  declarationsReady: number
  dataCheck: ShipmentResultCheckSummary
  heavyMetals: ShipmentResultCheckSummary
  svhc: ShipmentResultCheckSummary
}

export type ShipmentResultSkuGroup = {
  sku: string
  rows: PackagingShipmentResultRow[]
  summary: ShipmentResultSkuSummary
}

function summarizeCheck(labels: string[]): ShipmentResultCheckSummary {
  let attention = 0
  let notRun = 0
  let clear = 0
  for (const label of labels) {
    if (label === 'Requires Attention') {
      attention += 1
    } else if (label === 'Not run') {
      notRun += 1
    } else if (label === 'Clear') {
      clear += 1
    }
  }
  const worst =
    attention > 0
      ? 'Requires Attention'
      : notRun > 0
        ? 'Not run'
        : clear > 0
          ? 'Clear'
          : ''
  return { worst, attention, notRun, clear }
}

function summarizeSkuRows(
  rows: PackagingShipmentResultRow[]
): ShipmentResultSkuSummary {
  const packedIds = new Set<string>()
  let packedItem: { id: string; name: string } | null = null
  const suppliers = new Set<string>()
  let packagingCount = 0
  let declarationsReady = 0
  const dataCheckLabels: string[] = []
  const heavyMetalsLabels: string[] = []
  const svhcLabels: string[] = []

  for (const row of rows) {
    if (row.packedItemId != null) {
      packedIds.add(row.packedItemId)
      packedItem =
        packedIds.size === 1
          ? { id: row.packedItemId, name: row.packedItemName ?? '' }
          : null
    }
    if (row.supplierName != null && row.supplierName.length > 0) {
      suppliers.add(row.supplierName)
    }
    if (row.type === 'PACKAGING_ITEM') {
      packagingCount += 1
      if (row.declarationGenerated) {
        declarationsReady += 1
      }
      dataCheckLabels.push(row.dataCheckLabel)
      heavyMetalsLabels.push(row.heavyMetalsLabel)
      svhcLabels.push(row.svhcLabel)
    }
  }

  const supplierNames = [...suppliers]
  return {
    packedItemCount: packedIds.size,
    packedItem,
    supplierCount: suppliers.size,
    supplierName: supplierNames.length === 1 ? supplierNames[0] : null,
    packagingCount,
    declarationsReady,
    dataCheck: summarizeCheck(dataCheckLabels),
    heavyMetals: summarizeCheck(heavyMetalsLabels),
    svhc: summarizeCheck(svhcLabels),
  }
}

export function groupShipmentResultRowsBySku(
  rows: PackagingShipmentResultRow[]
): ShipmentResultSkuGroup[] {
  const groups: ShipmentResultSkuGroup[] = []
  for (const row of rows) {
    const current = groups[groups.length - 1]
    if (current == null || current.sku !== row.sku) {
      groups.push({ sku: row.sku, rows: [row], summary: summarizeSkuRows([row]) })
      continue
    }
    current.rows.push(row)
    current.summary = summarizeSkuRows(current.rows)
  }
  return groups
}
